import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DrawerFooter,
    Label,
    makeStyles,
    Tab,
    TabList,
    Toolbar,
    ToolbarButton,
    ToolbarGroup
} from '@fluentui/react-components';
import {
    DrawerOverlay,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderNavigation
} from "@fluentui/react-components/unstable";
import { DeleteRegular, Dismiss24Regular } from '@fluentui/react-icons';
import type { IGroupDetail, IIdentityDetails, SettingsData, IConfigurationInfo, IUserDetail, EAppId, IGraphPickerItem } from '../../interfaces';
import { EConfigurationInfoTab, EUserSelection } from '../../interfaces';
import { useMsal } from '@azure/msal-react';
import { getAppDisplayNameFromAppId, getUserAndGroupDetailsFromSelectedPeople } from '../../utilities/SettingsHelper/SettingsHelper';
import { useEffect, useRef, useState } from 'react';
import { UsersTab } from './UsersTab/UsersTab';
import { OverviewTab } from './OverviewTab/OverviewTab';
import { getDetailsForUsersAndGroupsAsync } from '../../apiRequests/graph/graph';
import { deleteConfigurationAsync, updateConfigurationAsync } from '../../apiRequests/settings/settings';
import { ProgressOverlay } from '../ProgressOverlay/ProgressOverlay';
import { SettingsEditor } from '../SettingsEditor/SettingsEditor';
import { InfoLabelWithLink } from '../InfoLabelWithLink/InfoLabelWithLink';

function getInitialUserSelection(hasUsers: boolean, hasGroups: boolean): EUserSelection {
    
    if (hasGroups) {
        return EUserSelection.Group;
    }

    return hasUsers 
        ? EUserSelection.User 
        : EUserSelection.Organization;
}

const useStyles = makeStyles({
    tabList: {
        paddingTop: "20px",
        paddingBottom: "20px"
    },
    toolbarButton: {
        marginLeft: "-14px"
    },
    drawerBodyLabel: {
        display: 'flex',
        columnGap: "10px"
    },
    mainContent: {
        paddingTop: "20px"
    },
    spinner: {
        position: "absolute",
        top: "50%",
        left: "50%"
    },
    header: {
        display: "flex",
        justifyContent: "end",
    },
    infoLabelDiv: {
        paddingBottom: '10px'
    }
});

export interface IConfigurationDataDrawerProps {
    configurationInfo: IConfigurationInfo;
    open: boolean;
    onOpenChange(isOpen: boolean, refresh: boolean): void;
}

export const ConfigurationDataDrawer = (props: IConfigurationDataDrawerProps) => {
    const { onOpenChange, open, configurationInfo } = props;
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const styles = useStyles();

    const [selectedTab, setSelectedTab] = useState(EConfigurationInfoTab.Overview);
    const [currentconfigurationInfo, setCurrentconfigurationInfo] = useState(configurationInfo);

    const [isLoading, setIsLoading] = useState(false);
    const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(true);

    const [selectedPeople, setSelectedPeople] = useState<IGraphPickerItem[]>([]);
    const [userDetails, setUserDetails] = useState<IUserDetail[]>([]);
    const [groupDetails, setGroupDetails] = useState<IGroupDetail[]>([]);
    const [usersToBeAdded, setUsersToBeAdded] = useState<IUserDetail[]>([]);
    const [groupsToBeAdded, setGroupsToBeAdded] = useState<IGroupDetail[]>([]);
    const [usersToBeRemoved, setUsersToBeRemoved] = useState<IUserDetail[]>([]);
    const [groupsToBeRemoved, setGroupsToBeRemoved] = useState<IGroupDetail[]>([]);

    const hasUsers = configurationInfo.userIds !== undefined && configurationInfo.userIds.length > 0;
    const hasGroups = configurationInfo.groupIds !== undefined && configurationInfo.groupIds.length > 0;
    const initialUserSelection = getInitialUserSelection(hasUsers, hasGroups);

    const [userSelection, setUserSelection] = useState(initialUserSelection);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    const [hasPendingChanges, setHasPendingChanges] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (open) {
            // reset state on opening the drawer
            setSelectedTab(EConfigurationInfoTab.Overview);
            setCurrentconfigurationInfo(configurationInfo);
            setUsersToBeAdded([]);
            setGroupsToBeAdded([]);
            setUsersToBeRemoved([]);
            setGroupsToBeRemoved([]);
            setSelectedPeople([]);
            setHasPendingChanges(false);
            setUserSelection(initialUserSelection);
        }
    }, [open, configurationInfo, hasGroups, hasUsers, initialUserSelection]);


    useEffect(() => {
        async function fetchData() {
            setIsUserDetailsLoading(true);
            let details: IIdentityDetails = {
                userDetails: [],
                groupDetails: []
            };

            try {
                abortControllerRef.current = new AbortController();
                details = await getDetailsForUsersAndGroupsAsync(instance, account, configurationInfo.userIds, configurationInfo.groupIds, abortControllerRef.current);
                setUserDetails(details.userDetails);
                setGroupDetails(details.groupDetails);
            } finally {
                setIsUserDetailsLoading(false);
            }
        }

        if (open) {
            fetchData().catch((error) => {
                console.error("Error fetching configuration data:", error);
            });
        } else {
            // abort any pending requests when the drawer is closed
            abortControllerRef.current?.abort('Aborted fetching configuration data');
        }

    }, [configurationInfo, instance, account, open]);


    const onOpenChangeInvoked = (isOpen: boolean): void => {
        onOpenChange(isOpen, false);
    };

    const onUserSelectionChanged = (value: EUserSelection): void => {
        setUserSelection(value);
        setHasPendingChanges(true);
    };

    const onOpenChanged = (isOpen: boolean): void => {
        onOpenChangeInvoked(isOpen);
    };

    const onSaveChanges = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const isOrganizationSelected = userSelection === EUserSelection.Organization;
            const userIds = isOrganizationSelected ? [] : [...userDetails.map(d => d.id), ...usersToBeAdded.map(u => u.id)];
            const groupIds = isOrganizationSelected ? [] : [...groupDetails.map(d => d.id), ...groupsToBeAdded.map(u => u.id)];

            await updateConfigurationAsync(instance, account, {
                ...currentconfigurationInfo,
                userIds,
                groupIds,
            });

        } catch (error) {
            console.debug("Error updating configuration: ", error);
        } finally {
            setIsLoading(false);
            onOpenChange(false, true);
        }
    };

    const onSettingsUpdated = (settings: SettingsData): void => {
        setCurrentconfigurationInfo((previous) => ({ ...previous, settingsData: settings }));
        setHasPendingChanges(true);
    };

    const onUsersRemoved = (userIds: string[]): void => {
        const userIdSet = new Set(userIds);
        const usersToRemove = userDetails.filter(d => !userIdSet.has(d.id));
        const updatedUserDetails = userDetails.filter(d => userIdSet.has(d.id));

        setUsersToBeRemoved((previous) => (previous.concat(usersToRemove)));
        setUserDetails(updatedUserDetails);
        setCurrentconfigurationInfo(previous => ({ ...previous, userIds: userIds }));
        setHasPendingChanges(true);
    };

    const onGroupsRemoved = (groupIds: string[]): void => {
        const groupIdSet = new Set(groupIds);
        const groupsToRemove = groupDetails.filter(d => !groupIdSet.has(d.id));
        const updatedGroupDetails = groupDetails.filter(d => groupIdSet.has(d.id));

        setGroupsToBeRemoved((previous) => (previous.concat(groupsToRemove)));
        setGroupDetails(updatedGroupDetails);
        setCurrentconfigurationInfo(previous => ({ ...previous, groupIds }));
        setHasPendingChanges(true);
    };

    const onEnabledChanged = (isEnabled: boolean): void => {
        setCurrentconfigurationInfo((previous) => ({ ...previous, isEnabled: isEnabled }));
        setHasPendingChanges(true);
    };

    const onDescriptionChanged = (description: string): void => {
        setCurrentconfigurationInfo((previous) => ({ ...previous, description: description }));
        setHasPendingChanges(true);
    };

    const onUserToBeRemovedRemoved = (userId: string): void => {
        setUsersToBeRemoved(previous => {
            const userDetail = previous.find(d => d.id === userId);
            if (!userDetail) {
                return previous;
            }

            setUserDetails(current => [...current, userDetail]);
            return previous.filter(u => u.id !== userId);
        });
    };

    const onGroupToBeRemovedRemoved = (groupId: string): void => {
        setGroupsToBeRemoved(previous => {
            const groupDetail = previous.find(d => d.id === groupId);
            if (!groupDetail) {
                return previous;
            }

            setGroupDetails(current => [...current, groupDetail]);
            return previous.filter(u => u.id !== groupId);
        });

    };

    const onRemoveConfiguration = async (): Promise<void> => {
        setShowConfirmationDialog(false);
        setIsLoading(true);
        try {
            await deleteConfigurationAsync(instance, account, configurationInfo.appId, configurationInfo.id);
        } catch (error) {
            console.debug(error);
        } finally {
            onOpenChange(false, true);
            setIsLoading(false);
        }
    };

    const onPeopleSelected = (people: IGraphPickerItem[]): void => {
        const result = getUserAndGroupDetailsFromSelectedPeople(people);
        setUsersToBeAdded(result.userDetails);
        setGroupsToBeAdded(result.groupDetails);
        setSelectedPeople(people);
        setHasPendingChanges(true);
    };

    const renderconfigurationInfoTabs = () => {
        switch (selectedTab) {
            case EConfigurationInfoTab.Overview:
                return (renderOverviewTab());

            case EConfigurationInfoTab.Settings:
                return (renderSettingsTab());

            case EConfigurationInfoTab.Users:
                return (renderUsersTab());

            default:
                return (<></>);
        }
    };

    const renderOverviewTab = () => {
        return (<OverviewTab
            userDetails={userDetails}
            groupDetails={groupDetails}
            isLoading={isUserDetailsLoading}
            configurationInfo={currentconfigurationInfo}
            onEnabledChange={onEnabledChanged}
            onDescriptionChange={onDescriptionChanged}
            onTabChange={(tab) => setSelectedTab(tab)} />);
    };

    const renderSettingsTab = () => {
        return (
            <>
                <div className={styles.infoLabelDiv}>
                    <InfoLabelWithLink appId={configurationInfo.appId as EAppId} />
                </div>
                <SettingsEditor
                    isLoading={isUserDetailsLoading}
                    applicationId={currentconfigurationInfo.appId as EAppId}
                    settingsData={currentconfigurationInfo.settingsData}
                    onSettingsUpdate={onSettingsUpdated}
                />
            </>);
    };

    const renderUsersTab = () => {
        return (<UsersTab
            isLoading={isUserDetailsLoading}
            userSelection={userSelection}
            configurationInfo={configurationInfo}
            userDetails={userDetails}
            groupDetails={groupDetails}
            selectedPeople={selectedPeople}
            usersToBeAdded={usersToBeAdded}
            groupsToBeAdded={groupsToBeAdded}
            usersToBeRemoved={usersToBeRemoved}
            groupsToBeRemoved={groupsToBeRemoved}
            onUserSelectionChange={onUserSelectionChanged}
            onPeopleSelect={onPeopleSelected}
            onGroupsRemove={onGroupsRemoved}
            onUsersRemove={onUsersRemoved}
            onUserToBeRemovedRemove={onUserToBeRemovedRemoved}
            onGroupToBeRemovedRemove={onGroupToBeRemovedRemoved}
        />);
    };


    const renderDrawerBodyHeader = () => {
        const appIdDisplay = currentconfigurationInfo.appId ?? "unkown";
        return (<div className={styles.drawerBodyLabel}>
            <Avatar color='brand' name={appIdDisplay} shape='square' size={72} />
            <div>
                <Label weight='semibold' size="large">{getAppDisplayNameFromAppId(currentconfigurationInfo.appId)}</Label>
                <div>{currentconfigurationInfo.id}</div>
                <div>
                    <Button appearance="transparent" icon={<DeleteRegular />} className={styles.toolbarButton} onClick={() => setShowConfirmationDialog(true)}>Remove</Button>
                </div>
            </div>
        </div>);
    };

    const renderDrawerBodyMainContent = () => {
        return (
            <div className={styles.mainContent}>
                <TabList selectedValue={selectedTab} className={styles.tabList} onTabSelect={(_, data) => { setSelectedTab(data.value as EConfigurationInfoTab); }}>
                    <Tab value={EConfigurationInfoTab.Overview}>Overview</Tab>
                    <Tab value={EConfigurationInfoTab.Settings}>Settings</Tab>
                    <Tab value={EConfigurationInfoTab.Users}>Users</Tab>
                </TabList>
                {renderconfigurationInfoTabs()}
            </div>
        );
    };

    return (
        <>
            <Dialog open={showConfirmationDialog}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Remove configuration?</DialogTitle>
                        <DialogContent>
                            Are you sure you want to remove this configuration for {getAppDisplayNameFromAppId(currentconfigurationInfo.appId)}?
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="primary" onClick={() => onRemoveConfiguration()}>Yes</Button>
                            <Button appearance="secondary" onClick={() => setShowConfirmationDialog(false)}>No</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <DrawerOverlay
                key={`configurationInfoDrawer_${configurationInfo?.id}`}
                size="large"
                position="end"
                open={open}
                onOpenChange={(_e, data) => onOpenChanged(data.open)}>
                {isLoading && <ProgressOverlay />}
                <DrawerHeader>
                    <DrawerHeaderNavigation className={styles.header}>
                        <Toolbar>
                            <ToolbarGroup>
                                <ToolbarButton
                                    appearance="subtle"
                                    aria-label="Close"
                                    disabled={isLoading}
                                    icon={<Dismiss24Regular />}
                                    onClick={() => onOpenChanged(false)}
                                />
                            </ToolbarGroup>
                        </Toolbar>
                    </DrawerHeaderNavigation>
                </DrawerHeader>
                <DrawerBody>
                    {renderDrawerBodyHeader()}
                    {renderDrawerBodyMainContent()}
                </DrawerBody>
                <DrawerFooter>
                    <Button
                        appearance="primary"
                        disabled={isLoading || !hasPendingChanges}
                        onClick={() => onSaveChanges()}>
                        Save changes
                    </Button>
                </DrawerFooter>
            </DrawerOverlay>
        </>
    );
};
